import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  Tooltip,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ArrowBackIcon, SettingsIcon } from '@chakra-ui/icons';
import { isSameDay } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import { getWAConversation, getWAMesages } from '../../../hooks/whatsapp';
import Composer from '../components/Composer';
import { WhatsappConversation, WhatsappMessage } from '../../../utils/types';
import {
  formatInboxDay,
  formatInboxTime,
  isWindowExpired,
  waBubbleOutgoing,
  WhatsAppDateSeparator,
  WhatsAppSyncPill,
} from '../components/whatsappInboxUi';

const normalizeMessages = (data: unknown): WhatsappMessage[] => {
  if (Array.isArray(data)) {
    return data as WhatsappMessage[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: WhatsappMessage[] }).results;
  }

  return [];
};

const messageStatusLabel: Record<string, string> = {
  queued: 'Queued',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: '!',
};

const MessageBubble = ({ message }: { message: WhatsappMessage }) => {
  const outgoing = message.direction === 'OUT';
  const failed = message.status === 'failed';
  const attachmentType = message.raw_payload?.type;
  const bubbleBg = outgoing ? (failed ? '#FEE2E2' : waBubbleOutgoing) : 'white';
  const bubbleColor = failed ? 'red.800' : 'gray.800';
  const statusColor = message.status === 'read' ? 'blue.400' : failed ? 'red.500' : 'gray.400';

  return (
    <Flex justify={outgoing ? 'flex-end' : 'flex-start'}>
      <Box
        bg={bubbleBg}
        color={bubbleColor}
        maxW={{ base: '86%', md: '72%' }}
        px={3.5}
        py={2.5}
        borderRadius="18px"
        borderBottomRightRadius={outgoing ? '6px' : '18px'}
        borderBottomLeftRadius={outgoing ? '18px' : '6px'}
        boxShadow="0 2px 10px rgba(15, 23, 42, 0.06)"
        borderWidth={failed ? '1px' : '0'}
        borderColor={failed ? 'red.200' : 'transparent'}
      >
        {attachmentType ? (
          <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={1}>
            {attachmentType}
          </Text>
        ) : null}
        <Text fontSize="sm" whiteSpace="pre-wrap">
          {message.body || 'No content'}
        </Text>
        <HStack justify="flex-end" spacing={1.5} mt={1.5}>
          <Text fontSize="11px" color="gray.500">
            {formatInboxTime(message.created_at)}
          </Text>
          {outgoing ? (
            <Text fontSize="11px" color={statusColor} fontWeight="700">
              {messageStatusLabel[message.status] || ''}
            </Text>
          ) : null}
        </HStack>
        {failed ? (
          <Text fontSize="11px" color="red.600" mt={1}>
            Message failed to send.
          </Text>
        ) : null}
      </Box>
    </Flex>
  );
};

export default function ConversationDetailSmall() {
  const { orgId, convId } = useParams();
  const [messages, setMessages] = useState<WhatsappMessage[]>([]);
  const [conversation, setConversation] = useState<WhatsappConversation | null>(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const fetchConversation = useCallback(async () => {
    if (!orgId || !convId) {
      return;
    }

    try {
      const [convRes, msgRes] = await Promise.all([
        getWAConversation(orgId, convId),
        getWAMesages(orgId, convId),
      ]);

      if (convRes.success) {
        setConversation(convRes.data);
      }

      if (msgRes.success) {
        setMessages(normalizeMessages(msgRes.data));
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    } finally {
      setLoading(false);
      setTimeout(() => scrollToBottom('auto'), 100);
    }
  }, [convId, orgId, scrollToBottom]);

  useEffect(() => {
    setLoading(true);
    fetchConversation();
  }, [fetchConversation]);

  useEffect(() => {
    if (!orgId || !convId) {
      return;
    }

    const interval = window.setInterval(() => {
      fetchConversation();
    }, 8000);

    return () => window.clearInterval(interval);
  }, [convId, fetchConversation, orgId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSent = (message: WhatsappMessage) => {
    setMessages((prev) => [...prev, message]);
    setTimeout(() => scrollToBottom(), 50);
  };

  const expired = isWindowExpired(conversation?.open_window_expires_at);
  const participantName = conversation?.lead?.first_name
    ? `${conversation.lead.first_name} ${conversation.lead.second_name || ''}`.trim()
    : conversation?.recipient_phone || 'Unknown contact';

  const groupedItems = useMemo(() => {
    const items: Array<{ type: 'date'; label: string } | { type: 'message'; message: WhatsappMessage }> = [];
    let lastDate: Date | null = null;

    messages.forEach((message) => {
      const currentDate = message.created_at ? new Date(message.created_at) : null;
      if (currentDate && (!lastDate || !isSameDay(currentDate, lastDate))) {
        items.push({ type: 'date', label: formatInboxDay(message.created_at) });
        lastDate = currentDate;
      }
      items.push({ type: 'message', message });
    });

    return items;
  }, [messages]);

  return (
    <Flex direction="column" h="100%" minH={{ base: '100vh', lg: '100%' }} bg={isMobile ? '#EFEAE2' : '#F0F2F5'}>
      <Flex
        align="center"
        justify="space-between"
        px={{ base: 3, md: 4 }}
        py={3}
        bg="#F7F8FA"
        borderBottomWidth="1px"
        borderColor="gray.200"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <HStack spacing={3} minW={0}>
          {isMobile ? (
            <IconButton
              aria-label="Back to chats"
              icon={<ArrowBackIcon />}
              variant="ghost"
              borderRadius="full"
              onClick={() => navigate(`/organization/${orgId}/inbox/whatsapp/conversations`)}
            />
          ) : null}
          <Avatar size="sm" name={participantName} bg="#128C7E" color="white" />
          <Box minW={0}>
            <Text fontWeight="700" color="gray.900" noOfLines={1}>
              {participantName}
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              <Text fontSize="xs" color="gray.500">
                {conversation?.recipient_phone || conversation?.lead?.phone || 'No phone'}
              </Text>
              <WhatsAppSyncPill label={expired ? '24h window expired' : 'Free-text allowed'} tone={expired ? 'orange' : 'green'} />
            </HStack>
          </Box>
        </HStack>

        <HStack spacing={1}>
          <Tooltip label="Settings">
            <IconButton
              aria-label="WhatsApp settings"
              icon={<SettingsIcon />}
              variant="ghost"
              borderRadius="full"
              onClick={() => navigate(`/organization/${orgId}/inbox/whatsapp/settings`)}
            />
          </Tooltip>
          <Tooltip label="Conversation deletion is not exposed because WhatsApp does not support safe remote deletion from this inbox">
            <Text fontSize="xs" color="gray.500" px={2}>
              Read-only thread controls
            </Text>
          </Tooltip>
        </HStack>
      </Flex>

      <Box
        flex={1}
        overflowY="auto"
        px={{ base: 3, md: 5 }}
        py={4}
        bgImage="radial-gradient(rgba(15,23,42,0.04) 1px, transparent 1px)"
        backgroundSize="18px 18px"
        bgColor="#EFEAE2"
      >
        {loading ? (
          <Flex justify="center" align="center" h="100%">
            <Spinner size="lg" />
          </Flex>
        ) : groupedItems.length === 0 ? (
          <VStack justify="center" align="center" h="100%" spacing={3} textAlign="center">
            <Text fontWeight="700" color="gray.800">
              No messages yet
            </Text>
            <Text fontSize="sm" color="gray.500" maxW="320px">
              When this contact sends or receives a message, the conversation history will appear here.
            </Text>
          </VStack>
        ) : (
          <Stack spacing={1.5}>
            {groupedItems.map((item, index) =>
              item.type === 'date' ? (
                <WhatsAppDateSeparator key={`date-${index}`} label={item.label} />
              ) : (
                <MessageBubble key={item.message.id || `${index}-${item.message.created_at}`} message={item.message} />
              ),
            )}
            <div ref={messagesEndRef} />
          </Stack>
        )}
      </Box>

      <Box
        px={{ base: 3, md: 4 }}
        py={{ base: 2.5, md: 3 }}
        bg="#F7F8FA"
        borderTopWidth="1px"
        borderColor="gray.200"
        position="sticky"
        bottom={0}
        zIndex={10}
      >
        {conversation && orgId && convId ? (
          <Composer
            conversationId={String(convId)}
            orgId={String(orgId)}
            recipient_phone={conversation.recipient_phone}
            onSent={handleSent}
            isWindowExpired={expired}
          />
        ) : null}
      </Box>

    </Flex>
  );
}
